import { useEffect, useRef, useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../api/client';
import MapaPiscinas from '../components/MapaPiscinas';
import { useFincaId } from '../context/FincaContext';
import {
  ICONO_ALIMENTADOR,
  ICONO_AIREADOR,
  ICONO_EQUIPO,
  COLORES_TIPO,
  NOMBRES_TIPO,
} from '../utils/iconosEquipos';

/* ✅ Mapeo de valores de registro diario a etiquetas legibles */
const textoModoAlimentacion = {
  cronograma: 'Cronograma',
  sonico:     'Automático',
  hibrido:    'Híbrido',
  automatico: 'Automático',
};

const textoNivelAlimentacion = {
  economico: 'Económico',
  normal:    'Normal',
  agresivo:  'Agresivo',
};

/* ✅ Helpers para fecha relativa del último registro diario */
function esDeHoy(fechaISO) {
  if (!fechaISO) return false;
  const hoy = new Date().toISOString().split('T')[0];
  const fecha = String(fechaISO).split('T')[0];
  return fecha === hoy;
}

function formatearDiaRelativo(fechaISO) {
  if (!fechaISO) return '';
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const fecha = new Date(fechaISO);
  fecha.setHours(0, 0, 0, 0);
  const diff = Math.floor((hoy - fecha) / 86400000);
  if (diff === 0) return 'hoy';
  if (diff === 1) return 'ayer';
  if (diff < 7) return `hace ${diff} días`;
  return `hace ${Math.floor(diff / 7)} sem`;
}

/* ✅ Normaliza cualquier valor (array, string JSON, null, objeto) a un array real */
function normalizarArrayEquipos(valor) {
  if (!valor) return [];
  if (Array.isArray(valor)) return valor;
  if (typeof valor === 'string') {
    const t = valor.trim();
    if (t === '' || t === 'null' || t === '{}' || t === '[]') return [];
    try {
      const parsed = JSON.parse(t);
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  }
  if (typeof valor === 'object') {
    const vals = Object.values(valor);
    return vals.filter(Boolean);
  }
  return [];
}

/* ✅ Normaliza una piscina completa (equipos + batimetría) */
function normalizarPiscina(p) {
  if (!p) return p;
  return {
    ...p,
    geometria_alimentadores: normalizarArrayEquipos(p.geometria_alimentadores),
    geometria_aireadores:    normalizarArrayEquipos(p.geometria_aireadores),
    geometria_equipos:       normalizarArrayEquipos(p.geometria_equipos),
    batimetria:              normalizarArrayEquipos(p.batimetria),
  };
}

export default function Piscinas() {
  const fincaIdContext = useFincaId();
  const [piscinas, setPiscinas] = useState([]);
  const [sectores, setSectores] = useState([]);
  const [sectorFiltro, setSectorFiltro] = useState('');
  const [tecnologiaFiltro, setTecnologiaFiltro] = useState('');
  const [dibujando, setDibujando] = useState(null);
  const [piscinaAEditar, setPiscinaAEditar] = useState(null);
  const [mensaje, setMensaje] = useState(null);
  const mapRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [piscinaSeleccionada, setPiscinaSeleccionada] = useState(null);
  const [siembraActiva, setSiembraActiva] = useState(null);
  const [modoEquipos, setModoEquipos] = useState(null);
  const [equiposTmp, setEquiposTmp] = useState({ alimentadores: [], aireadores: [], equipos: [] });
  const [guardandoEquipos, setGuardandoEquipos] = useState(false);

  const [modoBatimetria, setModoBatimetria] = useState(false);
  const [profundidadActual, setProfundidadActual] = useState('');
  const [batimetriaTmp, setBatimetriaTmp] = useState([]);
  const [guardandoBatimetria, setGuardandoBatimetria] = useState(false);

  const [mostrarAlimentadores, setMostrarAlimentadores] = useState(true);
  const [mostrarAireadores, setMostrarAireadores] = useState(true);
  const [mostrarEquipos, setMostrarEquipos] = useState(true);
  const [mostrarBatimetria, setMostrarBatimetria] = useState(true);
  const [mostrarTecnologias, setMostrarTecnologias] = useState(true);

  const [seccionAbierta, setSeccionAbierta] = useState(null);
  const [mostrarListaEquipos, setMostrarListaEquipos] = useState(false);

  /* ✅ Modal de equipos (alimentadores / aireadores / tolvas madres) */
  const [modalEquipos, setModalEquipos] = useState(null);

  /* ✅ Coordenadas manuales para agregar equipos */
  const [coordInput, setCoordInput] = useState({ lat: '', lng: '' });
  const [puntoTemporal, setPuntoTemporal] = useState(null);
  const [mostrarFormCoords, setMostrarFormCoords] = useState(false);

  useEffect(() => {
    if (fincaIdContext) cargarTodo(fincaIdContext);
    else { setSectores([]); setPiscinas([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fincaIdContext]);

  useEffect(() => {
    const editId = searchParams.get('editar');
    if (!editId || !Array.isArray(piscinas) || piscinas.length === 0) return;
    const p = piscinas.find((x) => x.id === editId);
    if (!p) return;
    setPiscinaAEditar(p);
    setMensaje({
      tipo: 'ok',
      texto: `📐 Editando la geometría de ${p.codigo}. Presiona "Dibujar geometría" y traza el polígono sobre el mapa.`,
    });
    if (p.geometria) setTimeout(() => mapRef.current?.centrarEnPiscina(p), 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [piscinas, searchParams]);

  useEffect(() => {
    if (modoEquipos) {
      setSeccionAbierta('equipos');
      setMostrarListaEquipos(true);
    }
  }, [modoEquipos]);

  useEffect(() => {
    if (modoBatimetria) {
      setSeccionAbierta('batimetria');
      setMostrarListaEquipos(true);
    }
  }, [modoBatimetria]);

  const cargarTodo = async (idFinca) => {
    try {
      const { data: secs } = await api.get(`/sectores/finca/${idFinca}`);
      const listaSecs = Array.isArray(secs) ? secs : [];
      setSectores(listaSecs);

      const { data: pis } = await api.get('/piscinas');
      const idsSectores = new Set(listaSecs.map((s) => s.id));
      const piscinasDeFinca = Array.isArray(pis)
        ? pis.filter((p) => idsSectores.has(p.sector_id)).map(normalizarPiscina)
        : [];
      setPiscinas(piscinasDeFinca);
      setMensaje(null);
    } catch (err) {
      console.error('Error cargando piscinas:', err);
      setPiscinas([]);
      setSectores([]);
      setMensaje({ tipo: 'error', texto: `Error al cargar: ${err.response?.data?.error || err.message}` });
    }
  };

  const tecnologiasUnicas = useMemo(() => {
    const set = new Set();
    piscinas.forEach((p) => {
      if (p.tecnologia && p.tecnologia.trim()) set.add(p.tecnologia.trim());
    });
    return Array.from(set).sort();
  }, [piscinas]);

  const piscinasFiltradas = useMemo(() => {
    if (!Array.isArray(piscinas)) return [];
    return piscinas.filter((p) => {
      if (sectorFiltro && p.sector_id !== sectorFiltro) return false;
      if (tecnologiaFiltro) {
        if (tecnologiaFiltro === '__sin__') {
          if (p.tecnologia && p.tecnologia.trim()) return false;
        } else if ((p.tecnologia || '').trim() !== tecnologiaFiltro) {
          return false;
        }
      }
      return true;
    });
  }, [piscinas, sectorFiltro, tecnologiaFiltro]);

  const conteos = useMemo(() => {
    const activas = piscinasFiltradas.filter((p) => p.activo).length;
    const cosechadas = piscinasFiltradas.filter((p) => !p.activo).length;
    return { activas, cosechadas, total: piscinasFiltradas.length };
  }, [piscinasFiltradas]);

  const activarDibujo = () => {
    if (!Array.isArray(sectores) || sectores.length === 0) {
      setMensaje({ tipo: 'error', texto: 'Primero crea un sector' });
      return;
    }
    setMensaje({
      tipo: 'ok',
      texto: piscinaAEditar
        ? `Dibuja el polígono para ${piscinaAEditar.codigo}. Haz clic en cada vértice y doble clic para cerrar.`
        : 'Dibuja el polígono sobre el mapa. Haz clic en cada vértice y doble clic para cerrar.',
    });
    mapRef.current?.activarDibujoPoligono();
  };

  const handleDibujar = async ({ geometria, area_ha, layer }) => {
    if (piscinaAEditar) {
      try {
        await api.put(`/piscinas/${piscinaAEditar.id}`, { geometria, area_ha });
        if (layer) layer.remove();
        setMensaje({ tipo: 'ok', texto: `✅ Geometría de ${piscinaAEditar.codigo} guardada.` });
        setPiscinaAEditar(null);
        setSearchParams({});
        await cargarTodo(fincaIdContext);
      } catch (err) {
        setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'Error al guardar la geometría' });
      }
      return;
    }
    setDibujando({ geometria, area_ha, layer });
    setMensaje(null);
  };

  const borrarGeometria = async () => {
    if (!piscinaAEditar) return;
    if (!confirm(`¿Borrar la geometría de ${piscinaAEditar.codigo}?`)) return;
    try {
      await api.put(`/piscinas/${piscinaAEditar.id}`, { geometria: null });
      setMensaje({ tipo: 'ok', texto: `✅ Geometría borrada.` });
      setPiscinaAEditar(null);
      setSearchParams({});
      await cargarTodo(fincaIdContext);
    } catch (err) {
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'Error al borrar' });
    }
  };

  const cancelarEdicion = () => {
    setPiscinaAEditar(null);
    setSearchParams({});
    setMensaje(null);
    mapRef.current?.cancelarDibujo();
  };

  const irAListaPiscinas = () => navigate('/configuracion');

  const seleccionarPiscina = async (p) => {
    if (piscinaAEditar) return;

    const pNorm = normalizarPiscina(p);

    setEquiposTmp({ alimentadores: [], aireadores: [], equipos: [] });
    setBatimetriaTmp([]);
    setModoEquipos(null);
    setModoBatimetria(false);
    setSiembraActiva(null);
    setMostrarListaEquipos(false);

    setPiscinaSeleccionada(pNorm);
    setSeccionAbierta(null);

    setEquiposTmp({
      alimentadores: Array.isArray(pNorm.geometria_alimentadores) ? [...pNorm.geometria_alimentadores] : [],
      aireadores:    Array.isArray(pNorm.geometria_aireadores)    ? [...pNorm.geometria_aireadores]    : [],
      equipos:       Array.isArray(pNorm.geometria_equipos)       ? [...pNorm.geometria_equipos]       : [],
    });
    setBatimetriaTmp(Array.isArray(pNorm.batimetria) ? [...pNorm.batimetria] : []);

    try {
      const { data } = await api.get(`/siembras/piscina/${p.id}/activa`);
      setSiembraActiva(data || null);
    } catch {
      setSiembraActiva(null);
    }
  };

  const cerrarPanel = () => {
    setPiscinaSeleccionada(null);
    setModoEquipos(null);
    setModoBatimetria(false);
    setSeccionAbierta(null);
    setEquiposTmp({ alimentadores: [], aireadores: [], equipos: [] });
    setBatimetriaTmp([]);
    setSiembraActiva(null);
    setProfundidadActual('');
    setMostrarListaEquipos(false);
  };

  const diasCultivo = (() => {
    if (!siembraActiva?.fecha_siembra) return null;
    const f1 = new Date();
    const f2 = new Date(siembraActiva.fecha_siembra);
    const diff = Math.floor((f1 - f2) / (1000 * 60 * 60 * 24));
    return diff < 0 ? 0 : diff;
  })();

  /* Cálculos de equipos y hectáreas */
  const areaHa = Number(piscinaSeleccionada?.area_ha) || 0;
  
  const totalHpAireadores = equiposTmp.aireadores.reduce((acc, aireador) => {
    return acc + (Number(aireador.hp) || 0);
  }, 0);

  const totalAireadores = equiposTmp.aireadores.length;

  const hpPorHectarea = areaHa > 0 ? (totalHpAireadores / areaHa) : 0;
  const aireadoresPorHectarea = areaHa > 0 ? (totalAireadores / areaHa) : 0;

  /* Agregar equipo por coordenadas manuales */
  const validarCoordenadas = (lat, lng) => {
    const latN = parseFloat(lat);
    const lngN = parseFloat(lng);
    if (isNaN(latN) || isNaN(lngN)) {
      return { valido: false, error: 'Las coordenadas deben ser números' };
    }
    if (latN < -90 || latN > 90) {
      return { valido: false, error: 'Latitud debe estar entre -90 y 90' };
    }
    if (lngN < -180 || lngN > 180) {
      return { valido: false, error: 'Longitud debe estar entre -180 y 180' };
    }
    return { valido: true, lat: latN, lng: lngN };
  };

  const previsualizarCoordenadas = () => {
    const { valido, error, lat, lng } = validarCoordenadas(coordInput.lat, coordInput.lng);
    if (!valido) {
      setMensaje({ tipo: 'error', texto: `❌ ${error}` });
      return;
    }
    setPuntoTemporal({
      lat,
      lng,
      nombre: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
    });
    setMensaje(null);
  };

  const agregarEquipoPorCoordenadas = () => {
    if (!modoEquipos) {
      setMensaje({ tipo: 'error', texto: '⚠️ Primero selecciona el tipo de equipo (Alimentador, Tolva Madre o Aireador)' });
      return;
    }
    const { valido, error, lat, lng } = validarCoordenadas(coordInput.lat, coordInput.lng);
    if (!valido) {
      setMensaje({ tipo: 'error', texto: `❌ ${error}` });
      return;
    }

    agregarEquipo({ lat, lng, tipo: modoEquipos });

    setCoordInput({ lat: '', lng: '' });
    setPuntoTemporal(null);
    setMensaje({ tipo: 'ok', texto: `✅ Equipo agregado en (${lat.toFixed(6)}, ${lng.toFixed(6)})` });
  };

  const usarUbicacionActual = () => {
    if (!navigator.geolocation) {
      setMensaje({ tipo: 'error', texto: 'Tu navegador no soporta geolocalización' });
      return;
    }
    setMensaje({ tipo: 'ok', texto: '📍 Obteniendo ubicación...' });
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoordInput({
          lat: pos.coords.latitude.toFixed(6),
          lng: pos.coords.longitude.toFixed(6),
        });
        setMensaje({ tipo: 'ok', texto: '✅ Coordenadas obtenidas' });
      },
      (err) => {
        setMensaje({ tipo: 'error', texto: `❌ Error: ${err.message}` });
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  /* Modal de equipos (alimentadores/aireadores/tolvas) */
  const abrirModalEquipos = (piscina, tipo) => {
    let items = [];
    let titulo = '';
    if (tipo === 'alimentadores') {
      items = normalizarArrayEquipos(piscina.geometria_alimentadores);
      titulo = `Alimentadores — ${piscina.codigo}`;
    } else if (tipo === 'aireadores') {
      items = normalizarArrayEquipos(piscina.geometria_aireadores);
      titulo = `Aireadores — ${piscina.codigo}`;
    } else if (tipo === 'equipos') {
      items = normalizarArrayEquipos(piscina.geometria_equipos);
      titulo = `Tolvas Madres — ${piscina.codigo}`;
    }
    setModalEquipos({ piscina, tipo, items, titulo });
  };

  const cerrarModalEquipos = () => setModalEquipos(null);

  const copiarCoordenadas = (item) => {
    const texto = `${item.lat}, ${item.lng}`;
    navigator.clipboard.writeText(texto);
    setMensaje({ tipo: 'ok', texto: `📋 Coordenadas copiadas: ${texto}` });
    setTimeout(() => setMensaje(null), 2000);
  };

  /* Manejo de equipos */
  const agregarEquipo = ({ lat, lng, tipo }) => {
    setEquiposTmp((prev) => {
      const key = tipo === 'alimentador' ? 'alimentadores'
                : tipo === 'aireador'    ? 'aireadores'
                : 'equipos';

      const listaActual = prev[key] || [];
      const duplicado = listaActual.some(
        (e) => Math.abs(e.lat - lat) < 0.00001 && Math.abs(e.lng - lng) < 0.00001
      );
      if (duplicado) return prev;

      const idx = listaActual.length + 1;
      const nuevo = { lat, lng, nombre: `${NOMBRES_TIPO[tipo]} #${idx}` };

      return { ...prev, [key]: [...listaActual, nuevo] };
    });
  };

  const renombrarEquipo = (tipo, idx, nuevoNombre) => {
    setEquiposTmp((prev) => {
      const key = tipo === 'alimentador' ? 'alimentadores' : tipo === 'aireador' ? 'aireadores' : 'equipos';
      const lista = [...prev[key]];
      lista[idx] = { ...lista[idx], nombre: nuevoNombre };
      return { ...prev, [key]: lista };
    });
  };

  const quitarAlimentador = (idx) => {
    setEquiposTmp((prev) => ({ ...prev, alimentadores: prev.alimentadores.filter((_, i) => i !== idx) }));
  };
  const quitarAireador = (idx) => {
    setEquiposTmp((prev) => ({ ...prev, aireadores: prev.aireadores.filter((_, i) => i !== idx) }));
  };
  const quitarEquipo = (idx) => {
    setEquiposTmp((prev) => ({ ...prev, equipos: prev.equipos.filter((_, i) => i !== idx) }));
  };

  const guardarEquipos = async () => {
    if (!piscinaSeleccionada) return;
    setGuardandoEquipos(true);
    try {
      await api.put(`/piscinas/${piscinaSeleccionada.id}/equipos`, {
        geometria_alimentadores: equiposTmp.alimentadores,
        geometria_aireadores: equiposTmp.aireadores,
        geometria_equipos: equiposTmp.equipos,
        cantidad_alimentadores: equiposTmp.alimentadores.length,
        cantidad_aireadores: equiposTmp.aireadores.length,
        cantidad_equipos: equiposTmp.equipos.length,
      });

      const { data: pis } = await api.get('/piscinas');
      const actualizada = Array.isArray(pis) ? pis.find((x) => x.id === piscinaSeleccionada.id) : null;
      if (actualizada) {
        const pNorm = normalizarPiscina(actualizada);
        setPiscinaSeleccionada(pNorm);
        setEquiposTmp({
          alimentadores: [...pNorm.geometria_alimentadores],
          aireadores:    [...pNorm.geometria_aireadores],
          equipos:       [...pNorm.geometria_equipos],
        });
      }

      await cargarTodo(fincaIdContext);
      setModoEquipos(null);
    } catch (err) {
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'Error al guardar equipos' });
    } finally {
      setGuardandoEquipos(false);
    }
  };

  /* Batimetría */
  const agregarPuntoBatimetria = ({ lat, lng, profundidad }) => {
    setBatimetriaTmp((prev) => [...prev, { lat, lng, profundidad }]);
  };
  const quitarPuntoBatimetria = (idx) => {
    setBatimetriaTmp((prev) => prev.filter((_, i) => i !== idx));
  };

  const guardarBatimetria = async () => {
    if (!piscinaSeleccionada) return;
    setGuardandoBatimetria(true);
    try {
      await api.put(`/piscinas/${piscinaSeleccionada.id}`, { batimetria: batimetriaTmp });

      const { data: pis } = await api.get('/piscinas');
      const actualizada = Array.isArray(pis) ? pis.find((x) => x.id === piscinaSeleccionada.id) : null;
      if (actualizada) {
        const pNorm = normalizarPiscina(actualizada);
        setPiscinaSeleccionada(pNorm);
        setBatimetriaTmp([...pNorm.batimetria]);
      }

      await cargarTodo(fincaIdContext);
      setModoBatimetria(false);
    } catch (err) {
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'Error al guardar batimetría' });
    } finally {
      setGuardandoBatimetria(false);
    }
  };

  const statsBatimetria = (() => {
    if (batimetriaTmp.length === 0) return null;
    const valores = batimetriaTmp.map((p) => Number(p.profundidad)).filter((n) => !isNaN(n));
    if (valores.length === 0) return null;
    const min = Math.min(...valores);
    const max = Math.max(...valores);
    const prom = valores.reduce((a, b) => a + b, 0) / valores.length;
    return { min, max, prom, count: valores.length };
  })();

  const toggleSeccion = (nombre) => {
    if (seccionAbierta === nombre) {
      setSeccionAbierta(null);
      setModoEquipos(null);
      setModoBatimetria(false);
    } else {
      setSeccionAbierta(nombre);
      if (nombre !== 'equipos') setModoEquipos(null);
      if (nombre !== 'batimetria') setModoBatimetria(false);
    }
  };

  const totalEquipos =
    equiposTmp.alimentadores.length +
    equiposTmp.aireadores.length +
    equiposTmp.equipos.length;

  /* Detectar cambios sin guardar */
  const hayCambiosSinGuardar = useMemo(() => {
    if (!piscinaSeleccionada) return false;
    const origAlims = piscinaSeleccionada.geometria_alimentadores || [];
    const origAirs  = piscinaSeleccionada.geometria_aireadores || [];
    const origEqs   = piscinaSeleccionada.geometria_equipos || [];
    if (origAlims.length !== equiposTmp.alimentadores.length) return true;
    if (origAirs.length  !== equiposTmp.aireadores.length)  return true;
    if (origEqs.length   !== equiposTmp.equipos.length)     return true;
    for (let i = 0; i < origAlims.length; i++) {
      if ((origAlims[i].nombre || '') !== (equiposTmp.alimentadores[i].nombre || '')) return true;
    }
    for (let i = 0; i < origAirs.length; i++) {
      if ((origAirs[i].nombre || '') !== (equiposTmp.aireadores[i].nombre || '')) return true;
    }
    for (let i = 0; i < origEqs.length; i++) {
      if ((origEqs[i].nombre || '') !== (equiposTmp.equipos[i].nombre || '')) return true;
    }
    return false;
  }, [piscinaSeleccionada, equiposTmp]);

  return (
    <div className="flex flex-col h-full w-full">
      <div className="p-4 bg-white shadow flex items-center gap-4 flex-wrap shrink-0 z-10 relative">
        <h1 className="text-2xl font-bold text-primary">Mapa</h1>

        {piscinaAEditar ? (
          <>
            <button onClick={activarDibujo} className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 font-medium">
              📐 {piscinaAEditar.geometria ? 'Redibujar' : 'Dibujar'} geometría de {piscinaAEditar.codigo}
            </button>
            {piscinaAEditar.geometria && (
              <button onClick={borrarGeometria} className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm">
                🗑️ Borrar geometría
              </button>
            )}
            <button onClick={cancelarEdicion} className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100 text-sm">
              ✕ Cancelar edición
            </button>
            <button onClick={irAListaPiscinas} className="px-4 py-2 border border-primary text-primary rounded hover:bg-blue-50 text-sm">
              ← Volver a Piscinas
            </button>
          </>
        ) : (
          <button onClick={activarDibujo} className="px-4 py-2 bg-primary text-white rounded hover:bg-blue-800">
            ✏️ Nueva piscina
          </button>
        )}

        <div className="ml-auto flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1">
            <label className="text-sm font-medium">Sector:</label>
            <select
              value={sectorFiltro}
              onChange={(e) => setSectorFiltro(e.target.value)}
              className="px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              disabled={!!piscinaAEditar}
            >
              <option value="">Todos</option>
              {sectores.map((s) => (
                <option key={s.id} value={s.id}>{s.nombre}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-1">
            <label className="text-sm font-medium">Tecnología:</label>
            <select
              value={tecnologiaFiltro}
              onChange={(e) => setTecnologiaFiltro(e.target.value)}
              className="px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary text-sm max-w-[200px]"
              disabled={!!piscinaAEditar}
            >
              <option value="">Todas</option>
              {tecnologiasUnicas.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
              <option value="__sin__">— Sin tecnología —</option>
            </select>
          </div>
        </div>
      </div>

      {!piscinaAEditar && (
        <div className="px-4 py-2 bg-white border-b flex items-center gap-2 flex-wrap text-xs shrink-0 z-10 relative">
          <span className="font-semibold text-gray-500 mr-1">👁️ Ver en el mapa:</span>

          <FiltroChip
            activo={mostrarTecnologias}
            onClick={() => setMostrarTecnologias(!mostrarTecnologias)}
            icono="🏷️"
            label="Etiquetas"
          />
          <FiltroChip
            activo={mostrarAlimentadores}
            onClick={() => setMostrarAlimentadores(!mostrarAlimentadores)}
            imgSrc={ICONO_ALIMENTADOR}
            label="Alimentadores"
          />
          <FiltroChip
            activo={mostrarAireadores}
            onClick={() => setMostrarAireadores(!mostrarAireadores)}
            imgSrc={ICONO_AIREADOR}
            label="Aireadores"
          />
          <FiltroChip
            activo={mostrarEquipos}
            onClick={() => setMostrarEquipos(!mostrarEquipos)}
            imgSrc={ICONO_EQUIPO}
            label="Tolva Madre"
          />
          <FiltroChip
            activo={mostrarBatimetria}
            onClick={() => setMostrarBatimetria(!mostrarBatimetria)}
            icono="🌊"
            label="Batimetría"
          />

          <div className="ml-auto flex items-center gap-3 text-gray-600">
            <span>Total: <strong className="text-gray-800">{conteos.total}</strong></span>
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm bg-green-600 inline-block"></span>
              Activas: <strong className="text-gray-800">{conteos.activas}</strong>
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm bg-gray-500 inline-block"></span>
              Cosechadas: <strong className="text-gray-800">{conteos.cosechadas}</strong>
            </span>
          </div>
        </div>
      )}

      {mensaje && (
        <div className={`mx-4 mt-2 p-3 rounded shrink-0 z-10 relative ${mensaje.tipo === 'ok' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {mensaje.texto}
        </div>
      )}

      {!piscinaAEditar && !piscinaSeleccionada && (
        <div className="mx-4 mt-2 p-3 bg-blue-50 text-blue-900 rounded text-sm shrink-0 z-10 relative">
          <strong>💡 Cómo usar el mapa:</strong>
          <ul className="list-disc list-inside mt-1">
            <li>Filtra por <strong>sector</strong> o <strong>tecnología</strong> para enfocarte en un grupo de piscinas.</li>
            <li>Las piscinas <strong>cosechadas</strong> aparecen en <strong style={{ color: '#6B7280' }}>gris con borde punteado</strong>.</li>
            <li>Haz clic sobre una piscina para agregar equipos o batimetría.</li>
          </ul>
        </div>
      )}

      {/* ✅ CONTENEDOR DEL MAPA CON ALTURA FLEXIBLE Y RELATIVA */}
      <div className="flex-1 relative w-full mx-4 mb-4 rounded shadow overflow-hidden z-0">
        <MapaPiscinas
          ref={mapRef}
          piscinas={piscinasFiltradas}
          onDibujar={handleDibujar}
          onClickPiscina={seleccionarPiscina}
          piscinaAEditar={piscinaAEditar}
          piscinaSeleccionada={piscinaSeleccionada}
          modoEquipos={modoEquipos}
          onAgregarEquipo={agregarEquipo}
          onEliminarEquipo={({ index, tipo }) => {
            if (tipo === 'alimentador') quitarAlimentador(index);
            else if (tipo === 'aireador') quitarAireador(index);
            else quitarEquipo(index);
          }}
          equiposTmp={equiposTmp}
          modoBatimetria={modoBatimetria}
          profundidadActual={profundidadActual}
          onAgregarPuntoBatimetria={agregarPuntoBatimetria}
          onEliminarPuntoBatimetria={quitarPuntoBatimetria}
          batimetriaTmp={batimetriaTmp}
          mostrarAlimentadores={mostrarAlimentadores}
          mostrarAireadores={mostrarAireadores}
          mostrarEquipos={mostrarEquipos}
          mostrarBatimetria={mostrarBatimetria}
          mostrarTecnologias={mostrarTecnologias}
          puntoTemporal={puntoTemporal}
        />

        {/* ✅ PANEL FLOTANTE (Anclado top-4 y bottom-4 para scroll garantizado) */}
        {piscinaSeleccionada && (
          <div className="absolute top-4 right-4 bottom-4 z-50 bg-white rounded-xl shadow-2xl border-2 border-primary w-[22rem] flex flex-col overflow-hidden">
            
            <div
              className="px-4 py-3 text-white flex items-center justify-between shrink-0"
              style={{ background: 'linear-gradient(to right, #0D47A1, #1565C0)' }}
            >
              <div>
                <h3 className="font-bold text-sm flex items-center gap-2 flex-wrap">
                  <span>Piscina # {piscinaSeleccionada.codigo}</span>
                  <span className="text-[11px] font-normal bg-white/20 px-1.5 py-0.5 rounded">
                    {piscinaSeleccionada.area_ha != null 
                      ? `${Number(piscinaSeleccionada.area_ha).toFixed(2)} ha` 
                      : '0.00 ha'}
                  </span>
                  {!piscinaSeleccionada.activo && (
                    <span className="bg-gray-200 text-gray-700 text-[10px] font-bold px-1.5 py-0.5 rounded">
                      COSECHADA
                    </span>
                  )}
                  {hayCambiosSinGuardar && (
                    <span className="bg-amber-400 text-amber-900 text-[10px] font-bold px-1.5 py-0.5 rounded">
                      sin guardar
                    </span>
                  )}
                </h3>
                <p className="text-xs mt-0.5" style={{ color: '#BBDEFB' }}>
                  Sector {piscinaSeleccionada.sector_nombre || '—'}
                </p>
              </div>
              <button onClick={cerrarPanel}
                className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-white/20 transition shrink-0 ml-2"
                style={{ color: '#fff', border: '1px solid rgba(255,255,255,0.3)' }}>
                ✕
              </button>
            </div>

            {/* ✅ CUERPO CON SCROLL INTERNO Y ESPACIADO MÁS COMPACTO (space-y-1.5) */}
            <div className="overflow-y-auto flex-1">
              <div className="p-4 space-y-1.5 text-sm pb-2">
                
                <div className="flex justify-between items-center border-b pb-1">
                  <span className="text-gray-500">Sobrevivencia:</span>
                  <strong className={
                    piscinaSeleccionada.sobrevivencia_estimada > 80 ? 'text-green-600' : 
                    piscinaSeleccionada.sobrevivencia_estimada > 60 ? 'text-orange-500' : 'text-red-600'
                  }>
                    {piscinaSeleccionada.sobrevivencia_estimada != null 
                      ? `${Number(piscinaSeleccionada.sobrevivencia_estimada).toFixed(1)} %` 
                      : '—'}
                  </strong>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-500">Días de cultivo:</span>
                  <strong>{diasCultivo != null ? `${diasCultivo} días` : '—'}</strong>
                </div>

                <div className="flex justify-between items-center gap-2">
                  <span className="text-gray-500 shrink-0">Último peso:</span>
                  <strong className="text-right flex items-center gap-1.5 justify-end min-w-0">
                    <span className="truncate">
                      {piscinaSeleccionada.ultimo_peso_g != null
                        ? `${Number(piscinaSeleccionada.ultimo_peso_g).toFixed(2)} g`
                        : '—'}
                    </span>
                    {piscinaSeleccionada.ultimo_peso_g != null && piscinaSeleccionada.ultimo_muestreo_fecha && (
                      <span
                        className={`text-[10px] font-normal px-1.5 py-0.5 rounded shrink-0 ${
                          esDeHoy(piscinaSeleccionada.ultimo_muestreo_fecha)
                            ? 'bg-green-100 text-green-700'
                            : 'bg-orange-100 text-orange-700'
                        }`}
                        title={`Último muestreo: ${piscinaSeleccionada.ultimo_muestreo_fecha}`}
                      >
                        {formatearDiaRelativo(piscinaSeleccionada.ultimo_muestreo_fecha)}
                      </span>
                    )}
                  </strong>
                </div>

                <div className="flex justify-between items-center gap-2">
                  <span className="text-gray-500 shrink-0">Biomasa semana:</span>
                  <strong className="text-right flex items-center gap-1.5 justify-end min-w-0">
                    <span className="truncate">
                      {piscinaSeleccionada.ultima_biomasa_kg != null
                        ? `${Number(piscinaSeleccionada.ultima_biomasa_kg).toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lb`
                        : '—'}
                    </span>
                    {piscinaSeleccionada.ultima_biomasa_kg != null && piscinaSeleccionada.ultimo_muestreo_fecha && (
                      <span
                        className={`text-[10px] font-normal px-1.5 py-0.5 rounded shrink-0 ${
                          esDeHoy(piscinaSeleccionada.ultimo_muestreo_fecha)
                            ? 'bg-green-100 text-green-700'
                            : 'bg-orange-100 text-orange-700'
                        }`}
                        title={`Último muestreo: ${piscinaSeleccionada.ultimo_muestreo_fecha}`}
                      >
                        {formatearDiaRelativo(piscinaSeleccionada.ultimo_muestreo_fecha)}
                      </span>
                    )}
                  </strong>
                </div>

                <div className="border-t border-gray-200 my-1" />

                <div className="flex justify-between items-center gap-2">
                  <span className="text-gray-500 shrink-0">Modo alimentación:</span>
                  <strong className="text-right flex items-center gap-1.5 justify-end min-w-0">
                    <span className="truncate">
                      {textoModoAlimentacion[piscinaSeleccionada.modo_alimentacion] || '—'}
                    </span>
                  </strong>
                </div>

                <div className="flex justify-between items-center gap-2">
                  <span className="text-gray-500 shrink-0">Nivel alimentación:</span>
                  <strong className="text-right flex items-center gap-1.5 justify-end min-w-0">
                    <span className="truncate">
                      {textoNivelAlimentacion[piscinaSeleccionada.nivel_alimentacion] || '—'}
                    </span>
                  </strong>
                </div>

                <div className="border-t border-gray-200 my-1" />

                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Tecnología:</span>
                  <strong className="text-right">{piscinaSeleccionada.tecnologia || '—'}</strong>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-gray-500"># Alimentadores:</span>
                  <strong className="flex items-center gap-1">
                    <img src={ICONO_ALIMENTADOR} alt="" className="w-4 h-4" />
                    {equiposTmp.alimentadores.length}
                  </strong>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Tolva Madre:</span>
                  <strong className="flex items-center gap-1">
                    <img src={ICONO_EQUIPO} alt="" className="w-4 h-4" />
                    {equiposTmp.equipos.length}
                  </strong>
                </div>

                <div className="border-t border-gray-200 my-1" />

                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Tipo Aireadores:</span>
                  <strong className="text-right text-xs">
                    {[...new Set(equiposTmp.aireadores.map(a => a.tipo_aireador))].filter(Boolean).join(', ') || '—'}
                  </strong>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Total Aireadores:</span>
                  <strong className="flex items-center gap-1">
                    <img src={ICONO_AIREADOR} alt="" className="w-4 h-4" />
                    {totalAireadores}
                  </strong>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Hp / Hectárea:</span>
                  <strong className="text-right text-cyan-700">
                    {hpPorHectarea.toFixed(2)} HP/ha
                  </strong>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Aireadores / Hectárea:</span>
                  <strong className="text-right text-cyan-700">
                    {aireadoresPorHectarea.toFixed(2)} /ha
                  </strong>
                </div>

                <div className="border-t border-gray-200 my-1" />

                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Batimetría:</span>
                  <strong className="text-cyan-700">{batimetriaTmp.length} puntos</strong>
                </div>

                <div className="border-t border-gray-200 my-1" />

              </div>

              <div className="border-t px-3 py-3 space-y-2 bg-gray-50">
                <button
                  type="button"
                  onClick={() => setMostrarListaEquipos(!mostrarListaEquipos)}
                  className="w-full flex items-center justify-between text-xs font-bold text-gray-500 uppercase hover:text-gray-800 transition px-1"
                >
                  <span>Equipos / Batimetría</span>
                  <span>{mostrarListaEquipos ? '🔼' : '🔽'}</span>
                </button>

                {mostrarListaEquipos && (
                  <>
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => toggleSeccion('equipos')}
                        className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-gray-50 transition"
                      >
                        <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                          🛠️ Equipos
                          {totalEquipos > 0 && (
                            <span className="bg-purple-100 text-purple-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                              {totalEquipos}
                            </span>
                          )}
                          {hayCambiosSinGuardar && (
                            <span className="bg-amber-100 text-amber-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                              sin guardar
                            </span>
                          )}
                        </span>
                        <span className="text-gray-400 text-xs">
                          {seccionAbierta === 'equipos' ? '▲' : '▼'}
                        </span>
                      </button>

                      {seccionAbierta === 'equipos' && (
                        <div className="px-3 pb-3 border-t bg-purple-50/30">
                          <div className="grid grid-cols-3 gap-1.5 mt-2">
                            <BotonTipoEquipo
                              tipo="alimentador"
                              modoEquipos={modoEquipos}
                              onClick={() => {
                                setModoEquipos(modoEquipos === 'alimentador' ? null : 'alimentador');
                                setModoBatimetria(false);
                              }}
                              color={COLORES_TIPO.alimentador}
                            />
                            <BotonTipoEquipo
                              tipo="equipo"
                              modoEquipos={modoEquipos}
                              onClick={() => {
                                setModoEquipos(modoEquipos === 'equipo' ? null : 'equipo');
                                setModoBatimetria(false);
                              }}
                              color={COLORES_TIPO.equipo}
                            />
                            <BotonTipoEquipo
                              tipo="aireador"
                              modoEquipos={modoEquipos}
                              onClick={() => {
                                setModoEquipos(modoEquipos === 'aireador' ? null : 'aireador');
                                setModoBatimetria(false);
                              }}
                              color={COLORES_TIPO.aireador}
                            />
                          </div>

                          <div className="mt-3 bg-white rounded-lg border border-gray-200 overflow-hidden">
                            <button
                              type="button"
                              onClick={() => setMostrarFormCoords(!mostrarFormCoords)}
                              className="w-full px-3 py-2 flex items-center justify-between hover:bg-gray-50 text-xs font-semibold text-gray-700"
                            >
                              <span>📌 Ingresar coordenadas manualmente</span>
                              <span className="text-gray-400">{mostrarFormCoords ? '▲' : '▼'}</span>
                            </button>

                            {mostrarFormCoords && (
                              <div className="px-3 pb-3 border-t bg-gray-50">
                                <div className="grid grid-cols-2 gap-2 mt-2">
                                  <label className="block">
                                    <span className="text-[10px] text-gray-500">Latitud</span>
                                    <input
                                      type="number"
                                      step="0.000001"
                                      value={coordInput.lat}
                                      onChange={(e) => setCoordInput({ ...coordInput, lat: e.target.value })}
                                      placeholder="-3.258100"
                                      className="mt-1 w-full px-2 py-1 border rounded text-xs"
                                    />
                                  </label>
                                  <label className="block">
                                    <span className="text-[10px] text-gray-500">Longitud</span>
                                    <input
                                      type="number"
                                      step="0.000001"
                                      value={coordInput.lng}
                                      onChange={(e) => setCoordInput({ ...coordInput, lng: e.target.value })}
                                      placeholder="-79.955300"
                                      className="mt-1 w-full px-2 py-1 border rounded text-xs"
                                    />
                                  </label>
                                </div>

                                <div className="flex gap-1.5 mt-2">
                                  <button
                                    type="button"
                                    onClick={usarUbicacionActual}
                                    className="flex-1 px-2 py-1.5 bg-blue-500 text-white rounded text-[11px] hover:bg-blue-600"
                                    title="Usar mi ubicación actual"
                                  >
                                    📍 Mi ubicación
                                  </button>
                                  <button
                                    type="button"
                                    onClick={previsualizarCoordenadas}
                                    disabled={!coordInput.lat || !coordInput.lng}
                                    className="flex-1 px-2 py-1.5 bg-amber-500 text-white rounded text-[11px] hover:bg-amber-600 disabled:opacity-50"
                                  >
                                    👁️ Previsualizar
                                  </button>
                                </div>

                                <button
                                  type="button"
                                  onClick={agregarEquipoPorCoordenadas}
                                  disabled={!modoEquipos || !coordInput.lat || !coordInput.lng}
                                  className="w-full mt-1.5 px-3 py-2 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 disabled:opacity-50"
                                >
                                  ➕ Agregar equipo en estas coordenadas
                                </button>

                                {!modoEquipos && (
                                  <p className="text-[10px] text-orange-600 mt-1 text-center">
                                    ⚠️ Primero elige el tipo de equipo arriba
                                  </p>
                                )}
                              </div>
                            )}
                          </div>

                          {modoEquipos && (
                            <p className="text-[11px] text-gray-600 mt-2">
                              💡 Haz clic en el mapa para colocar el equipo.
                            </p>
                          )}

                          <ListaEquipos
                            tipo="alimentador"
                            items={equiposTmp.alimentadores}
                            onRenombrar={(i, v) => renombrarEquipo('alimentador', i, v)}
                            onQuitar={quitarAlimentador}
                            color={COLORES_TIPO.alimentador}
                          />
                          <ListaEquipos
                            tipo="equipo"
                            items={equiposTmp.equipos}
                            onRenombrar={(i, v) => renombrarEquipo('equipo', i, v)}
                            onQuitar={quitarEquipo}
                            color={COLORES_TIPO.equipo}
                          />
                          <ListaEquipos
                            tipo="aireador"
                            items={equiposTmp.aireadores}
                            onRenombrar={(i, v) => renombrarEquipo('aireador', i, v)}
                            onQuitar={quitarAireador}
                            color={COLORES_TIPO.aireador}
                          />

                          <button
                            type="button"
                            onClick={guardarEquipos}
                            disabled={guardandoEquipos}
                            className="mt-3 w-full px-3 py-2 bg-primary text-white rounded text-xs font-medium hover:bg-blue-800 disabled:opacity-50"
                          >
                            {guardandoEquipos ? 'Guardando...' : `💾 Guardar equipos`}
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => toggleSeccion('batimetria')}
                        className="w-full px-3 py-2.5 flex items-center justify-between hover:bg-gray-50 transition"
                      >
                        <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                          🌊 Batimetría
                          {batimetriaTmp.length > 0 && (
                            <span className="bg-cyan-100 text-cyan-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                              {batimetriaTmp.length}
                            </span>
                          )}
                        </span>
                        <span className="text-gray-400 text-xs">
                          {seccionAbierta === 'batimetria' ? '▲' : '▼'}
                        </span>
                      </button>

                      {seccionAbierta === 'batimetria' && (
                        <div className="px-3 pb-3 border-t bg-cyan-50/30">
                          <div className="flex gap-2 mt-2">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={profundidadActual}
                              onChange={(e) => setProfundidadActual(e.target.value)}
                              placeholder="Prof. (m)"
                              className="w-24 px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/30"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setModoBatimetria(!modoBatimetria);
                                setModoEquipos(null);
                              }}
                              disabled={!profundidadActual || isNaN(Number(profundidadActual))}
                              className={`flex-1 px-3 py-1.5 rounded text-xs font-medium transition border ${
                                modoBatimetria
                                  ? 'bg-cyan-600 text-white border-cyan-700'
                                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 disabled:opacity-50'
                              }`}
                            >
                              {modoBatimetria ? '✓ Colocando puntos' : '📍 Colocar en el mapa'}
                            </button>
                          </div>

                          {modoBatimetria && (
                            <p className="text-[11px] text-cyan-700 mt-2">
                              💡 Cada clic agrega un punto con <strong>{Number(profundidadActual).toFixed(2)} m</strong>.
                            </p>
                          )}

                          {statsBatimetria && (
                            <div className="grid grid-cols-3 gap-1 mt-2">
                              <div className="bg-white rounded p-1.5 border border-cyan-200 text-center">
                                <div className="text-[10px] text-gray-500">Mín</div>
                                <div className="text-xs font-bold text-cyan-700">{statsBatimetria.min.toFixed(2)}</div>
                              </div>
                              <div className="bg-white rounded p-1.5 border border-cyan-200 text-center">
                                <div className="text-[10px] text-gray-500">Prom</div>
                                <div className="text-xs font-bold text-cyan-700">{statsBatimetria.prom.toFixed(2)}</div>
                              </div>
                              <div className="bg-white rounded p-1.5 border border-cyan-200 text-center">
                                <div className="text-[10px] text-gray-500">Máx</div>
                                <div className="text-xs font-bold text-cyan-700">{statsBatimetria.max.toFixed(2)}</div>
                              </div>
                            </div>
                          )}

                          {batimetriaTmp.length > 0 && (
                            <div className="mt-2">
                              <div className="text-[11px] font-semibold text-gray-500 mb-1">
                                Puntos registrados ({batimetriaTmp.length})
                              </div>
                              <ul className="space-y-0.5 max-h-24 overflow-y-auto">
                                {batimetriaTmp.map((p, i) => (
                                  <li key={i} className="flex items-center justify-between text-[11px] bg-white rounded px-2 py-1">
                                    <span>🌊 {Number(p.profundidad).toFixed(2)} m</span>
                                    <button onClick={() => quitarPuntoBatimetria(i)} className="text-red-600 hover:underline">
                                      ✕
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          <button
                            type="button"
                            onClick={guardarBatimetria}
                            disabled={guardandoBatimetria}
                            className="mt-3 w-full px-3 py-2 bg-cyan-600 text-white rounded text-xs font-medium hover:bg-cyan-700 disabled:opacity-50"
                          >
                            {guardandoBatimetria ? 'Guardando...' : `💾 Guardar batimetría`}
                          </button>

                          {batimetriaTmp.length > 0 && (
                            <button
                              type="button"
                              onClick={() => {
                                if (window.confirm('¿Borrar TODOS los puntos de batimetría de esta piscina?')) {
                                  setBatimetriaTmp([]);
                                }
                              }}
                              className="w-full mt-1 text-red-600 text-[11px] hover:underline"
                            >
                              🗑️ Borrar todos los puntos
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {dibujando && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Nueva piscina</h2>
            <p className="text-xs text-gray-600 mb-4">
              Área calculada desde el polígono: <strong>{dibujando.area_ha} ha</strong>
            </p>
            <NuevaPiscinaForm
              dibujando={dibujando}
              sectores={sectores}
              sectorFiltro={sectorFiltro}
              onCancelar={() => {
                if (dibujando.layer) dibujando.layer.remove();
                setDibujando(null);
              }}
              onGuardado={async () => {
                if (dibujando.layer) dibujando.layer.remove();
                setDibujando(null);
                await cargarTodo(fincaIdContext);
                setMensaje({ tipo: 'ok', texto: 'Piscina creada correctamente' });
              }}
              onError={(msg) => setMensaje({ tipo: 'error', texto: msg })}
            />
          </div>
        </div>
      )}

      {modalEquipos && (
        <div
          className="fixed inset-0 md:left-64 z-[9999] flex items-center justify-center p-4"
          style={{
            backgroundColor: 'rgba(75, 85, 99, 0.6)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
          onClick={cerrarModalEquipos}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
            style={{
              border: '2px solid #0D47A1',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="px-5 py-3 text-white flex items-center justify-between"
              style={{
                background: 'linear-gradient(to right, #0D47A1, #1565C0)',
                borderBottom: '2px solid #0D47A1',
              }}
            >
              <div>
                <h2 className="text-sm font-bold">{modalEquipos.titulo}</h2>
                <p className="text-xs" style={{ color: '#BBDEFB' }}>
                  {modalEquipos.items.length} equipo
                  {modalEquipos.items.length === 1 ? '' : 's'} registrado
                  {modalEquipos.items.length === 1 ? '' : 's'}
                </p>
              </div>
              <button
                onClick={cerrarModalEquipos}
                className="rounded-full w-7 h-7 flex items-center justify-center transition text-xs"
                style={{
                  color: 'rgba(255,255,255,0.9)',
                  border: '1px solid rgba(255,255,255,0.3)',
                }}
                aria-label="Cerrar"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {modalEquipos.items.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  No hay equipos registrados
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 text-gray-600">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold w-12">#</th>
                      <th className="text-left px-3 py-2 font-semibold">Nombre</th>
                      <th className="text-right px-3 py-2 font-semibold">Latitud</th>
                      <th className="text-right px-3 py-2 font-semibold">Longitud</th>
                      <th className="text-center px-3 py-2 font-semibold w-24">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalEquipos.items.map((item, i) => (
                      <tr
                        key={i}
                        className="border-t border-gray-100 hover:bg-blue-50/40"
                      >
                        <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                        <td className="px-3 py-2 font-medium">
                          {item.nombre || `Equipo #${i + 1}`}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-xs">
                          {Number(item.lat).toFixed(6)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-xs">
                          {Number(item.lng).toFixed(6)}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button
                            type="button"
                            onClick={() => copiarCoordenadas(item)}
                            className="text-blue-600 hover:text-blue-800 text-xs"
                            title="Copiar coordenadas"
                          >
                            📋 Copiar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div
              className="px-5 py-3 flex justify-end"
              style={{ backgroundColor: '#F9FAFB', borderTop: '1px solid #E5E7EB' }}
            >
              <button
                type="button"
                onClick={cerrarModalEquipos}
                className="px-5 py-1.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-blue-800"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ✅ SUB-COMPONENTES (Deben estar FUERA del componente Piscinas) */

function FiltroChip({ activo, onClick, icono, imgSrc, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border transition font-medium ${
        activo
          ? 'bg-blue-600 text-white border-blue-700 shadow-sm'
          : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
      }`}
      title={label}
    >
      {imgSrc ? (
        <img src={imgSrc} alt="" className="w-4 h-4" style={{ filter: activo ? 'brightness(0) invert(1)' : 'none' }} />
      ) : (
        <span className="text-sm">{icono}</span>
      )}
      <span>{label}</span>
      {activo ? <span className="text-[10px]">✓</span> : <span className="text-[10px] opacity-50">○</span>}
    </button>
  );
}

function BotonTipoEquipo({ tipo, modoEquipos, onClick, color }) {
  const activo = modoEquipos === tipo;
  const imgSrc = tipo === 'alimentador' ? ICONO_ALIMENTADOR : tipo === 'aireador' ? ICONO_AIREADOR : ICONO_EQUIPO;
  const label = NOMBRES_TIPO[tipo];

  return (
    <button
      type="button"
      onClick={onClick}
      className="px-1 py-2 rounded text-[10px] font-medium transition border flex flex-col items-center justify-center gap-1"
      style={{
        backgroundColor: activo ? color : '#ffffff',
        borderColor: activo ? color : '#D1D5DB',
        color: activo ? '#ffffff' : '#374151',
      }}
    >
      <img src={imgSrc} alt="" className="w-5 h-5" />
      <span className="leading-tight">{activo ? '✓' : label}</span>
    </button>
  );
}

function ListaEquipos({ tipo, items, onRenombrar, onQuitar, color }) {
  if (items.length === 0) return null;

  const imgSrc = tipo === 'alimentador' ? ICONO_ALIMENTADOR : tipo === 'aireador' ? ICONO_AIREADOR : ICONO_EQUIPO;

  return (
    <div className="mt-3">
      <div className="text-[11px] font-semibold mb-1 flex items-center gap-1.5" style={{ color }}>
        <img src={imgSrc} alt="" className="w-3.5 h-3.5" />
        {NOMBRES_TIPO[tipo]}s ({items.length})
        <span className="text-[9px] text-gray-400 font-normal">— clic en el nombre para editar</span>
      </div>
      <ul className="space-y-1 max-h-32 overflow-y-auto">
        {items.map((item, i) => (
          <li key={i} className="flex items-center gap-1 text-[11px] bg-white rounded border border-gray-200 px-1.5 py-1">
            <input
              type="text"
              value={item.nombre || `${NOMBRES_TIPO[tipo]} #${i + 1}`}
              onChange={(e) => onRenombrar(i, e.target.value)}
              className="flex-1 min-w-0 px-1 py-0.5 border border-transparent hover:border-gray-300 focus:border-blue-400 focus:outline-none rounded text-[11px] bg-transparent"
              title="Editar nombre"
            />
            <button
              onClick={() => onQuitar(i)}
              className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded px-1 font-bold"
              title="Eliminar"
            >
              ✕
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function NuevaPiscinaForm({ dibujando, sectores, sectorFiltro, onCancelar, onGuardado, onError }) {
  const [codigo, setCodigo] = useState('');
  const [area, setArea] = useState(dibujando?.area_ha?.toString() || '');
  const [sectorId, setSectorId] = useState(sectorFiltro || (Array.isArray(sectores) && sectores[0]?.id) || '');
  const [guardando, setGuardando] = useState(false);

  const guardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      await api.post('/piscinas', {
        codigo,
        area_ha: area ? parseFloat(area) : null,
        sector_id: sectorId,
        geometria: dibujando.geometria,
      });
      await onGuardado();
    } catch (err) {
      onError(err.response?.data?.error || 'Error al crear');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <form onSubmit={guardar}>
      <label className="block mb-3">
        <span className="text-sm font-medium">Código / Nombre</span>
        <input
          type="text"
          value={codigo}
          onChange={(e) => setCodigo(e.target.value)}
          placeholder="Ej: P-05"
          className="mt-1 w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
          required
          autoFocus
        />
      </label>
      <label className="block mb-3">
        <span className="text-sm font-medium">Área (ha)</span>
        <input
          type="number"
          step="0.01"
          value={area}
          onChange={(e) => setArea(e.target.value)}
          className="mt-1 w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </label>
      <label className="block mb-4">
        <span className="text-sm font-medium">Sector</span>
        <select
          value={sectorId}
          onChange={(e) => setSectorId(e.target.value)}
          className="mt-1 w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary"
          required
        >
          {sectores.map((s) => (
            <option key={s.id} value={s.id}>{s.nombre}</option>
          ))}
        </select>
      </label>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancelar} className="px-4 py-2 border rounded hover:bg-gray-100" disabled={guardando}>
          Cancelar
        </button>
        <button type="submit" className="px-4 py-2 bg-primary text-white rounded hover:bg-blue-800" disabled={guardando}>
          {guardando ? 'Guardando...' : 'Guardar piscina'}
        </button>
      </div>
    </form>
  );
}